// DesignationsFlow — the classification-SCD verb flow (ADR-060 §3): every
// overlay the site's "Update classification" act opens (the act is a row of the
// site's registry — `SiteActions.tsx`, ADR-036 §7 — a chooser asking "what
// happened?", ADR-038 §1). Mirrors the solution timeline's grammar (fork, not
// extraction) with the designation specifics: a period is the
// COMPLETE classification state (type + priority required, category optional),
// so the form is three selects; backdating is first-class; per-period verbs
// (correct data · correct start date · delete) live on every history row — the
// drawer's and the peek's, one menu.
//
// The chooser's three verbs (one-plain-sentence consequences):
//   • The classification changed — a new period since a date ≤ today (an old
//     date splits the containing period).
//   • Correct data — repair the current period's values in place.
//   • Correct start date — move one boundary (the previous close follows).
// A never-classified site gets a single "Classify site" act instead (the
// first-values PUT — only pre-SCD legacy rows can be here).
// Per-period verbs (correct · start-date · delete) ride EVERY history row —
// the peek's inline history AND the drawer's (never a read-only peek: the
// solution timeline trained "verbs live on the rows"; one grammar).
//
// SHAPE: the form + row-menu are PURE (props in, intents out — the
// reconcile-kit precedent, so the Workbench story composes the shipped
// components); the form's three fields are the family's pickers over the
// vocabularies' small lists (ADR-080 §3 — loaded on the press, nothing asked
// while the editor is closed; the type under its tier's heading with the seats);
// the useDesignationsFlow HOOK owns the hooks, overlay state, mutations and
// toasts — ONE overlay state shared by the act (through `useSiteActions`) and
// the peek's history rows.
//
// The form holds the SITE it writes beneath (ADR-084 §4 — a period is a write
// beneath the site): the first values and a correction carry the site's stamp as
// the form opened, and a stale save shows the line with Load the latest (the
// site's detail re-read; a corrected period gone meanwhile is the form's row
// gone). A change is a new period — it carries none.
//
// site:write gates the act (the registry hides it otherwise); a DRAFT's
// classification is the wizard's job — the act never shows on a draft.

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { StartDateDialog, deleteConsequence, displayEnd } from '../../components/patterns/ReconcilePeekBody.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DatePicker } from '../../components/ui/DatePicker.js';
import { IconButton } from '../../components/ui/IconButton.js';
import { Label } from '../../components/ui/Label.js';
import { Menu, MenuItem, MenuSeparator } from '../../components/ui/Menu.js';
import { Picker } from '../../components/patterns/Picker.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { useToast } from '../../components/ui/Toast.js';
import { icons } from '../../components/ui/icons.js';
import { formatDate } from '../../lib/format.js';
import {
  fetchSiteDetail,
  sitesKeys,
  useDeleteDesignationPeriod,
  useDesignationChange,
  useDesignationCorrection,
  useDesignationStartDateCorrection,
  useWriteDesignations,
} from '../../hooks/useSites.js';
import { siteTypesSource, useSiteTypeTiers } from '../../hooks/useSiteTypes.js';
import { sitePrioritiesSource } from '../../hooks/useSitePriorities.js';
import { siteCategoriesSource } from '../../hooks/useSiteCategories.js';
import type { SmallSource } from '../../hooks/useOptions.js';
import { useWeekStart } from '../../hooks/useWeekStart.js';
import { todayIn } from '../../lib/period.js';
import type { DesignationValues } from '@ems-app/schemas/sites/requests';
import type { DesignationPeriod, SiteDetail } from '@ems-app/schemas/sites/responses';
import { tierHeading } from './SiteRecordFields.js';


export type DesignationMode = 'first' | 'changed' | 'correct';
/** The site the timeline hangs on — its stamp rides the first values and a correction (ADR-084 §4). */
export type DesignationSite = Pick<SiteDetail, 'id' | 'row_version' | 'designation_history'>;
export type DesignationOverlay =
  | { kind: 'form'; mode: DesignationMode; periodId: number | null }
  | { kind: 'startDate'; periodId: number; stamp?: number }
  | { kind: 'delete'; periodId: number }
  | null;

/** One period's values line — "Type · Priority · Category" (category only when set). */
export function designationValuesLine(p: DesignationPeriod): string {
  return [p.site_type.name, p.site_priority.name, p.site_category?.name].filter(Boolean).join(' · ');
}

/** The per-period ⋯ menu — ONE component for every history row (the peek's
 *  inline history + the drawer's), so the verb set can never drift. */
export function DesignationPeriodMenu({
  period,
  deleteBlocked,
  onOpen,
  t,
}: {
  period: DesignationPeriod;
  deleteBlocked: boolean;
  onOpen: (overlay: DesignationOverlay) => void;
  t: TFunction;
}) {
  const isCurrent = period.effective_to === null;
  return (
    <Menu
      label={t('reconcile.peek.rowActions')}
      renderTrigger={() => (
        <IconButton aria-label={t('reconcile.peek.rowActions')}>
          <icons.moreHorizontal size={16} aria-hidden />
        </IconButton>
      )}
    >
      <MenuItem icon={<icons.edit size={16} aria-hidden />} onAction={() => onOpen({ kind: 'form', mode: 'correct', periodId: period.id })}>
        {t('reconcile.menu.correctData')}
      </MenuItem>
      <MenuItem icon={<icons.calendar size={16} aria-hidden />} onAction={() => onOpen({ kind: 'startDate', periodId: period.id })}>
        {t('reconcile.menu.correctDate')}
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        tone="danger"
        icon={<icons.delete size={16} aria-hidden />}
        isDisabled={isCurrent && deleteBlocked}
        onAction={() => onOpen({ kind: 'delete', periodId: period.id })}
      >
        {t('reconcile.delete.confirm')}
      </MenuItem>
    </Menu>
  );
}

// ── the ONE designation form (PURE: first · changed · correct) + the
//    per-period verbs (drawer-hosted) ─────────────────────────────────────────

interface FormValues {
  site_type_id: number | null;
  site_priority_id: number | null;
  site_category_id: number | null;
  effective_from: string | null;
  change_reason: string;
}

export interface DesignationFormProps {
  mode: DesignationMode;
  /** correct mode's target period (null in first/changed). */
  periodId: number | null;
  /** The site the form writes beneath — its timeline (newest first) seeds the form at each open, its stamp rides the save. */
  site: DesignationSite;
  /** Load the latest: the fresh site, or null when it (or the corrected period) is gone. */
  reload: () => Promise<DesignationSite | null>;
  siteName: string;
  /** The project whose vocabularies the three pickers ask (each on the press). */
  projectId: string;
  /** The site's zone — "today", the latest allowed start, is the site's day (ADR-082 §6), never the machine's. */
  timeZone?: string;
  /** A non-draft site must keep its classification — its only period's delete is blocked. */
  deleteBlocked: boolean;
  /** Persist the act with the site's stamp as the form opened; throwing surfaces on the shell's refusal line (errors.json). */
  onSubmit: (values: DesignationValues, meta: { effective_from: string | null; change_reason: string | null; stamp: number | undefined }) => Promise<void>;
  /** Per-period verbs retarget THIS overlay (drawer-hosted). */
  onRetarget: (o: DesignationOverlay) => void;
  onClose: () => void;
}

export function DesignationForm({ mode, periodId, site, reload, siteName, projectId, timeZone, deleteBlocked, onSubmit, onRetarget, onClose }: DesignationFormProps) {
  const { t } = useTranslation('sites');
  const history = site.designation_history;
  // The three vocabularies' sources (ADR-080 §3) — the type under its tier's heading with the seats (the tiers observed
  // on the picker's own entry, never asked here).
  const typeSource = useMemo(() => siteTypesSource(projectId), [projectId]);
  const prioritySource = useMemo(() => sitePrioritiesSource(projectId), [projectId]);
  const categorySource = useMemo(() => siteCategoriesSource(projectId), [projectId]);
  const tiers = useSiteTypeTiers(projectId, { enabled: false }).data ?? [];
  const tierLabel = (code: string): string => {
    const tier = tiers.find((x) => x.code === code);
    return tier ? tierHeading(tier, t) : code;
  };

  const today = todayIn(timeZone ?? 'UTC');
  const weekStart = useWeekStart(projectId);
  const target = mode === 'correct' ? (history.find((p) => p.id === periodId) ?? null) : null;
  // A change must land inside known history; a start on an existing boundary is
  // a correction instead (the service refuses both — say it before save).
  const earliestStart = history.length ? history[history.length - 1]!.effective_from : null;
  const boundaries = useMemo(() => new Set(history.map((p) => p.effective_from)), [history]);

  const dated = mode !== 'correct';
  const schema = useMemo(
    () =>
      z.object({
        site_type_id: z.number({ message: ' ' }).int().positive(),
        site_priority_id: z.number({ message: ' ' }).int().positive(),
        site_category_id: z.number().int().positive().nullable(),
        effective_from: dated
          ? z
              .string({ message: t('reconcile.form.dateRequired') })
              .refine((v) => v <= today, { message: t('reconcile.form.dateFuture') })
              .refine((v) => mode !== 'changed' || earliestStart === null || v >= earliestStart, {
                message: t('reconcile.form.dateBeforeHistory', { date: earliestStart ? formatDate(earliestStart, 'date') : '' }),
              })
              .refine((v) => mode !== 'changed' || !boundaries.has(v), { message: t('designations.form.onBoundary') })
          : z.string().nullable(),
        change_reason: z.string().trim().max(500),
      }) as unknown as z.ZodType<FormValues, FormValues>,
    [dated, mode, today, earliestStart, boundaries, t],
  );

  const shell = useFormShell<FormValues, DesignationSite>({
    schema,
    row: site,
    // The seed: the corrected period's values, else the current one's — from the site as it is at the open.
    values: (r) => {
      const h = r?.designation_history ?? [];
      const seed = mode === 'correct' ? h.find((p) => p.id === periodId) : h.find((p) => p.is_current);
      return {
        site_type_id: seed?.site_type.id ?? null,
        site_priority_id: seed?.site_priority.id ?? null,
        site_category_id: seed?.site_category?.id ?? null,
        effective_from: mode === 'changed' ? null : today,
        change_reason: '',
      };
    },
    reload,
    onSubmit: async (v, { stamp }) => {
      await onSubmit(
        { site_type_id: v.site_type_id!, site_priority_id: v.site_priority_id!, site_category_id: v.site_category_id },
        { effective_from: v.effective_from, change_reason: v.change_reason.trim() || null, stamp },
      );
    },
    onSuccess: onClose,
  });

  const title =
    mode === 'first'
      ? t('designations.classify')
      : mode === 'changed'
        ? t('designations.menu.changed')
        : t('reconcile.form.correctTitle', { date: formatDate(target?.effective_from ?? today, 'date') });
  const submitLabel =
    mode === 'first' ? t('designations.classify') : mode === 'changed' ? t('reconcile.form.submitChanged') : t('reconcile.form.submitCorrect');

  const pick = (
    name: 'site_type_id' | 'site_priority_id' | 'site_category_id',
    label: string,
    source: SmallSource,
    clearable: boolean,
    groupLabel?: (group: string) => string,
  ) => (
    <Controller
      control={shell.form.control}
      name={name}
      render={({ field, fieldState }) => (
        <Picker
          source={source}
          label={label}
          required={!clearable}
          groupLabel={groupLabel}
          value={field.value == null ? null : String(field.value)}
          onChange={(v) => field.onChange(v == null ? null : Number(v))}
          clearable={clearable}
          error={fieldState.error ? fieldState.error.message || ' ' : undefined}
        />
      )}
    />
  );

  return (
    <DrawerForm shell={shell} open onClose={onClose} title={title} description={siteName} submitLabel={submitLabel}>
      {/* A period is the COMPLETE classification state — the three axes together. */}
      {pick('site_type_id', t('filters.type'), typeSource, false, tierLabel)}
      {pick('site_priority_id', t('filters.priority'), prioritySource, false)}
      {pick('site_category_id', t('filters.category'), categorySource, true)}

      {dated && (
        <>
          <Controller
            control={shell.form.control}
            name="effective_from"
            render={({ field, fieldState }) => (
              <DatePicker
                label={t('reconcile.form.sinceWhen')}
                value={field.value}
                onChange={field.onChange}
                maxValue={today}
                weekStart={weekStart}
                zone={timeZone}
                error={fieldState.error?.message}
              />
            )}
          />
          <div>
            <Label htmlFor="des-reason">{t('reconcile.form.reason')}</Label>
            <Textarea id="des-reason" rows={2} {...shell.form.register('change_reason')} />
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('designations.form.reasonHelp')}</p>
          </div>
        </>
      )}

      {/* The per-period verbs live HERE too (the peek's history rows carry the same menu). */}
      {history.length > 0 && (
        <div className="border-t border-[var(--color-border)] pt-4">
          <p className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">{t('designations.history')}</p>
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {history.map((p) => {
              const isCurrent = p.effective_to === null;
              const label = isCurrent
                ? t('reconcile.peek.current', { date: formatDate(p.effective_from, 'date') })
                : t('reconcile.peek.previous', {
                    from: formatDate(p.effective_from, 'date'),
                    to: formatDate(displayEnd(p.effective_to!), 'date'),
                  });
              return (
                <li key={p.id} className="flex items-start justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <span className={isCurrent ? 'font-medium' : ''}>{label}</span>
                    <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{designationValuesLine(p)}</div>
                    {p.change_reason && (
                      <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{p.change_reason}</div>
                    )}
                  </div>
                  <DesignationPeriodMenu period={p} deleteBlocked={deleteBlocked} onOpen={onRetarget} t={t} />
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </DrawerForm>
  );
}

// ── the HOST HOOK: overlay state + mutations + toasts. ONE overlay state shared
//    by the act (`useSiteActions`) and the peek's inline history's row menus. ──

export interface DesignationsFlowProps {
  projectId: string;
  /** The site — its designation timeline (newest first, the detail payload) and the stamp its writes carry. */
  site: DesignationSite;
  /** The site's zone — the start-date dialog's "today" is the site's day. */
  timeZone?: string;
  siteName: string;
  /** The site's lifecycle status — the only-period delete guard (draft excepted). */
  status: string;
}

export function useDesignationsFlow({ projectId, site, siteName, status, timeZone }: DesignationsFlowProps) {
  const { t } = useTranslation('sites');
  const toast = useToast();
  const qc = useQueryClient();
  const siteId = site.id;
  const history = site.designation_history;
  const [overlay, setOverlay] = useState<DesignationOverlay>(null);
  // The start-date dialog takes the site's stamp as it opens — a refetch while it is open never moves it.
  const openOverlay = (next: DesignationOverlay) => setOverlay(next?.kind === 'startDate' ? { ...next, stamp: site.row_version } : next);

  const write = useWriteDesignations(projectId);
  const weekStart = useWeekStart(projectId);
  const change = useDesignationChange(projectId);
  const correct = useDesignationCorrection(projectId);
  const startDate = useDesignationStartDateCorrection(projectId);
  const deletePeriod = useDeleteDesignationPeriod(projectId);

  // A non-draft site must keep its classification (the activation CHECK's mirror
  // parity) — its only period's delete is server-blocked (ONLY_PERIOD_UNDELETABLE).
  const deleteBlocked = status !== 'draft' && history.length <= 1;


  const submit = async (mode: DesignationMode, periodId: number | null, values: DesignationValues, meta: { effective_from: string | null; change_reason: string | null; stamp: number | undefined }) => {
    if (mode === 'correct') {
      await correct.mutateAsync({ siteId, periodId: periodId!, body: { ...values, row_version: meta.stamp } });
      toast.success(t('designations.toast.corrected'));
    } else if (mode === 'changed') {
      // A new period beneath the site — an add, no stamp on the wire.
      await change.mutateAsync({ siteId, body: { ...values, effective_from: meta.effective_from!, change_reason: meta.change_reason } });
      toast.success(t('designations.toast.changed', { date: formatDate(meta.effective_from!, 'date') ?? meta.effective_from! }));
    } else {
      await write.mutateAsync({ siteId, body: { ...values, row_version: meta.stamp, effective_from: meta.effective_from ?? undefined, change_reason: meta.change_reason } });
      toast.success(t('designations.toast.classified'));
    }
  };

  // Load the latest: the site's detail re-read quietly, into the page's entry once it answers; a corrected period
  // gone meanwhile is the form's row gone.
  const reload = (mode: DesignationMode, periodId: number | null) => async (): Promise<DesignationSite | null> => {
    const fresh = await readLatest(qc, sitesKeys.detail(projectId, siteId), () => fetchSiteDetail(projectId, siteId, true));
    return mode === 'correct' && !fresh.designation_history.some((p) => p.id === periodId) ? null : fresh;
  };

  const overlays = (
    <>
      {overlay?.kind === 'form' && (
        <DesignationForm
          key={`${overlay.mode}:${overlay.periodId ?? 'new'}`}
          mode={overlay.mode}
          periodId={overlay.periodId}
          site={site}
          reload={reload(overlay.mode, overlay.periodId)}
          siteName={siteName}
          projectId={projectId}
          timeZone={timeZone}
          deleteBlocked={deleteBlocked}
          onSubmit={(values, meta) => submit(overlay.mode, overlay.periodId, values, meta)}
          onRetarget={openOverlay}
          onClose={() => setOverlay(null)}
        />
      )}

      {overlay?.kind === 'startDate' && history.length > 0 && (
        <StartDateDialog
          history={history}
          periodId={overlay.periodId}
          timeZone={timeZone}
          weekStart={weekStart}
          keys="designations.startDate"
          onClose={() => setOverlay(null)}
          // One value corrected on what the dialog shows — it carries the site's stamp as the dialog OPENED; a
          // refusal stays in the dialog, said by the confirm (a stale one: close, the page has re-read, set it again).
          onSubmit={(effectiveFrom) => startDate.mutateAsync({ siteId, periodId: overlay.periodId, effectiveFrom, rowVersion: overlay.stamp }).then(() => void toast.success(t('designations.toast.dateFixed')))}
          t={t}
        />
      )}

      {overlay?.kind === 'delete' && (
        <ConfirmDialog
          open
          onClose={() => setOverlay(null)}
          title={t('designations.delete.title')}
          description={deleteConsequence(history, overlay.periodId, t, 'designations.delete')}
          confirmLabel={t('reconcile.delete.confirm')}
          tone="danger"
          onConfirm={() => deletePeriod.mutateAsync({ siteId, periodId: overlay.periodId }).then(() => void toast.success(t('designations.toast.deleted')))}
        />
      )}
    </>
  );

  return { openOverlay, overlays, deleteBlocked };
}
